/* eslint-disable no-case-declarations */
import cloneDeep from "lodash/cloneDeep"
import i18next from "i18next"
import { ProviderService } from "./provider"
import { Service } from "./Service"
import GedApi from "../API/ged"
import StructUtils from "../utils/structUtils"
import { LocalStorage, LocalStore, SharedDataLocal } from "../models/store"
import EnvUtils from "../utils/envUtils"

class StoreService extends Service {
    static SHARED_DATA_SRC = "sharedData"

    static LOCAL_STORAGE = "localStorage"

    static NAV_PARAM_SRC = "navParam"

    static LANG = "lang"

    static GED_SRC = "ged"

    static THIS = "this"

    private static instance: StoreService

    private platform: "web" | "mobile" = EnvUtils.getPlatform()

    private sqliteService: any

    // données locales
    private sharedData: any = { session: {} }

    constructor() {
        super()
        // set le nom du service
        this.name = "StoreService"
        // ajoute toute les methodes du service au tableau de mapping
        this.serviceMap = [
            { key: "get", fct: this.get },
            { key: "set", fct: this.set },
            { key: "push", fct: this.push },
            { key: "unpush", fct: this.unpush },
            { key: "delete", fct: this.delete },
        ]
    }

    /**
     * Récuperation de l'instance
     */
    public static getInstance(): StoreService {
        if (!this.instance) {
            this.instance = new StoreService()
        }
        return this.instance
    }

    /**
     * Ajoute la dot notation SharedData prefix à la clef
     * @param key
     */
    public static useSharedDataKey(key: string): string {
        return `${StoreService.SHARED_DATA_SRC}.${key}`
    }

    /**
     * Utilise le Provider Service pour récupèrer / modifier des données dynamiques à partir de Service
     * @param source nom du service
     * @param endpoint nom du endPoint
     * @param params paramètre à fournir au endpoint
     */
    public static dataFromService(
        source: string,
        endpoint: string,
        params: Array<unknown> = [],
        data?: unknown
    ): Promise<any> {
        const promise = new Promise((resolve, reject) => {
            // récuperation du type de service
            const serviceType: typeof Service | undefined =
                ProviderService.getInstance().get(source)

            // récuperation d l'instance du service
            const service: Service = serviceType?.getInstance()

            // si le service existe
            if (service) {
                // récuperation du endPoint
                const serviceEndPoint = service.fromMap(endpoint)
                // si il y a des data à poster on les ajouter en tete des paramètres
                if (data) params.unshift(data)
                // appel de la fonction endPoint
                const result = serviceEndPoint?.apply(service, params)
                // test si le fonction est une promise
                if (result && result.then) {
                    // si promise on relais la reponse
                    result.then(
                        (resultData: unknown) => resolve(resultData),
                        (err: unknown) => reject(err)
                    )
                } else {
                    // si pas promise on le renvoie juste
                    resolve(result)
                }
            }
        })

        return promise
    }

    /**
     * Retourne la donnée qui correspond à la clef
     * @param key Clef de la données
     * @param params paramètre à fournir au endpoint
     * @param context paramètre utilisé pour le type de source #this
     */
    public get(
        key: string,
        params?: Array<unknown>,
        context?: any
    ): Promise<any> {
        const promise = new Promise((resolve, reject) => {
            const paramsArray = params
            // parse de la dot notation
            const [sourcePrefix, trimedKey, optionalSubKeysAttribut] =
                StructUtils.extractSourceKey(key)

            // get des data en fonction des types de sources disponibles
            if (sourcePrefix && trimedKey) {
                switch (sourcePrefix) {
                    case StoreService.SHARED_DATA_SRC:
                        if (optionalSubKeysAttribut) {
                            resolve(
                                StructUtils.getValueFromArray(
                                    this.sharedData[trimedKey],
                                    optionalSubKeysAttribut
                                )
                            )
                        } else {
                            resolve(this.sharedData[trimedKey])
                        }
                        break
                    case StoreService.LOCAL_STORAGE:
                        this.persistant
                            .get(trimedKey, optionalSubKeysAttribut)
                            .then(resolve, reject)
                        break
                    case StoreService.LANG:
                        const translation = i18next.t(trimedKey)
                        resolve(translation)
                        break
                    case StoreService.THIS:
                        if (context) {
                            if (optionalSubKeysAttribut) {
                                resolve(
                                    StructUtils.getValueFromArray(
                                        context[trimedKey],
                                        optionalSubKeysAttribut
                                    )
                                )
                            } else {
                                resolve(context[trimedKey])
                            }
                        }
                        resolve(null)
                        break
                    case StoreService.NAV_PARAM_SRC:
                        const navParamService =
                            ProviderService.getInstance().get(
                                "NavigationParamService"
                            )
                        const navParam = navParamService
                            ? navParamService.getInstance()
                            : null

                        if (!navParam) throw Error("No navigationParam found")
                        const parsedNavParam = navParam.get()
                        const navParamsObject: any =
                            parsedNavParam.params as any
                        if (parsedNavParam.params) {
                            const resolvedNavParam =
                                navParamsObject[trimedKey] ||
                                typeof navParamsObject[trimedKey] ===
                                    "number" ||
                                typeof navParamsObject[trimedKey] === "boolean"
                                    ? navParamsObject[trimedKey]
                                    : null
                            resolve(resolvedNavParam)
                        } else reject(Error("No params found"))

                        break
                    case StoreService.GED_SRC:
                        GedApi.download(trimedKey).then(
                            (data) => {
                                resolve(URL.createObjectURL(data))
                            },
                            (err) => reject(err)
                        )
                        break
                    default:
                        StoreService.dataFromService(
                            sourcePrefix,
                            trimedKey,
                            paramsArray
                        ).then(
                            (res) => {
                                if (optionalSubKeysAttribut)
                                    resolve(
                                        StructUtils.getValueFromArray(
                                            res,
                                            optionalSubKeysAttribut
                                        )
                                    )
                                resolve(res)
                            },
                            (err) => reject(err)
                        )
                }
            } else if (
                sourcePrefix &&
                (sourcePrefix === StoreService.SHARED_DATA_SRC ||
                    sourcePrefix === StoreService.THIS)
            ) {
                if (sourcePrefix === StoreService.THIS) {
                    resolve(context)
                }
                resolve(this.sharedData)
            } else {
                reject(Error("Wrong key when trying to get data from store"))
            }
        })

        return promise
    }

    /**
     * Supprime la donnée qui correspond à la clef
     * @param key Clef de la données
     */
    public delete(key: string): Promise<any> {
        const promise = new Promise((resolve, reject) => {
            // parse de la dot notation
            const [sourcePrefix, trimedKey] = StructUtils.extractSourceKey(key)

            // delete les data en fonction des types de sources disponibles
            if (sourcePrefix && trimedKey) {
                if (sourcePrefix === StoreService.SHARED_DATA_SRC) {
                    this.local.delete(trimedKey)
                    resolve("deleted!")
                } else if (sourcePrefix === StoreService.LOCAL_STORAGE) {
                    return this.persistant.delete(trimedKey)
                }
            } else {
                reject(Error("Wrong key when trying to get data from store"))
            }
        })

        return promise
    }

    /**
     * Set la donnée qui correspond à la clef donnée
     * @param key Clef de la données (clef simple "mon_endpoint" ou dot notation "ma_source.mon_endpoint")
     * @param data donnée
     * @param source source de la donnée
     * @param params paramètre à fournir au endpoint
     */
    public set(
        key: string,
        data: unknown,
        params?: Array<unknown>
    ): Promise<any> {
        const promise = new Promise((resolve, reject) => {
            // parse de la dot notation
            let [sourcePrefix, trimedKey, optionalSubKeysAttribut] =
                StructUtils.extractSourceKey(key)

            if (sourcePrefix && trimedKey) {
                switch (sourcePrefix) {
                    case StoreService.SHARED_DATA_SRC:
                        if (optionalSubKeysAttribut?.length)
                            trimedKey += `.${optionalSubKeysAttribut.join(".")}`
                        this.local.set(trimedKey, data)
                        resolve(data)
                        break
                    case StoreService.LOCAL_STORAGE:
                        this.persistant
                            .set(trimedKey, data)
                            .then(() => resolve(data), reject)
                        break
                    case StoreService.NAV_PARAM_SRC:
                        // dans une queryParams on ne veux que des type simple à mettre dans l'url
                        if (
                            typeof data === "string" ||
                            typeof data === "boolean" ||
                            typeof data === "number"
                        ) {
                            const queryParamObject: any = {}
                            queryParamObject[key] = data
                            const navParamService =
                                ProviderService.getInstance().get(
                                    "NavigationParamService"
                                )
                            const navParam = navParamService
                                ? navParamService.getInstance()
                                : null
                            if (!navParam)
                                throw Error("No navigationParam found")
                            const url = navParam.get()

                            navParam.set(url.urlPageName, queryParamObject)

                            resolve(data)
                        }
                        break
                    default:
                        StoreService.dataFromService(
                            sourcePrefix,
                            trimedKey,
                            params,
                            data
                        ).then(
                            (res) => resolve(res),
                            (err) => reject(err)
                        )
                }
            } else {
                reject(Error("Wrong key when trying to set data from store"))
            }
        })
        return promise
    }

    /**
     * Push la donnée qui correspond à la clef (NB. pour SHARED_DATA)
     * @param key Clef de la données (clef simple "mon_endpoint" ou dot notation "ma_source.mon_endpoint")
     * @param data donnée
     */
    public push(key: string, data: unknown): Promise<any> {
        const promise = new Promise((resolve, reject) => {
            // parse de la dot notation
            const [sourcePrefix, trimedKey, optionalSubKeysAttribut] =
                StructUtils.extractSourceKey(key)

            if (
                sourcePrefix &&
                trimedKey &&
                sourcePrefix === StoreService.SHARED_DATA_SRC
            ) {
                if (optionalSubKeysAttribut) {
                    const arr = StructUtils.getValueFromArray(
                        this.sharedData[trimedKey],
                        optionalSubKeysAttribut
                    ) as Array<any>

                    arr.push(data)
                } else this.local.push(trimedKey, data)
                resolve(data)
            }
            reject(Error("Wrong key when trying to set data from store"))
        })
        return promise
    }

    /**
     * unpush la donnée qui correspond à la clef (NB. pour SHARED_DATA)
     * @param key Clef de la données (clef simple "mon_endpoint" ou dot notation "ma_source.mon_endpoint")
     * @param data donnée
     */
    public unpush(key: string, data: unknown): Promise<any> {
        const promise = new Promise((resolve, reject) => {
            // parse de la dot notation
            const [sourcePrefix, trimedKey, optionalSubKeysAttribut] =
                StructUtils.extractSourceKey(key)

            if (
                sourcePrefix &&
                trimedKey &&
                sourcePrefix === StoreService.SHARED_DATA_SRC
            ) {
                if (optionalSubKeysAttribut) {
                    const arr = StructUtils.getValueFromArray(
                        this.sharedData[trimedKey],
                        optionalSubKeysAttribut
                    ) as Array<any>
                    const index = StructUtils.findMultiTypes(data, arr)

                    if (index > -1) arr.splice(index, 1)
                } else this.local.unpush(trimedKey, data)
                resolve(data)
            }
            reject(Error("Wrong key when trying to set data from store"))
        })
        return promise
    }

    /**
     * fait réference au SharedData : mémoire partagé temporaire de l'app
     */
    public local: LocalStore & SharedDataLocal = {
        get: (key: string) => {
            if (key.indexOf(".") !== -1) {
                const keys = StructUtils.extractSourceKey(key)
                return keys.length &&
                    keys[0] &&
                    keys[1] &&
                    Object.prototype.hasOwnProperty.call(
                        this.sharedData,
                        keys[0]
                    )
                    ? this.sharedData[keys[0]][keys[1]]
                    : null
            }
            return this.sharedData[key]
        },
        set: (key: string, data: any) => {
            if (key.indexOf(".") !== -1) {
                const parts = key.split(".")
                const path =
                    parts[0] === StoreService.SHARED_DATA_SRC
                        ? parts.slice(1)
                        : parts

                let current: any = this.sharedData
                for (let i = 0; i < path.length - 1; i += 1) {
                    const segment = path[i]
                    if (
                        current[segment] === undefined ||
                        current[segment] === null ||
                        typeof current[segment] !== "object"
                    ) {
                        current[segment] = {}
                    }
                    current = current[segment]
                }
                current[path[path.length - 1]] = cloneDeep(data)
                return
            }
            this.sharedData[key] = cloneDeep(data)
        },
        push: (key: string, data: any) => {
            if (this.sharedData[key] === undefined) {
                this.sharedData[key] = [data]
            } else {
                this.sharedData[key].push(data)
            }
            return this.sharedData[key]
        },
        unpush: (key: string, data: any) => {
            if (this.sharedData[key] && this.sharedData[key].length) {
                const index = StructUtils.findMultiTypes(
                    data,
                    this.sharedData[key]
                )

                if (index > -1) this.sharedData[key].splice(index, 1)
            }

            return null
        },
        getSession: (key: string) => {
            return this.sharedData.session[key]
        },
        setSession: (key: string, data: any) => {
            this.sharedData.session[key] = data
        },
        delete: (key: string) => {
            this.sharedData[key] = undefined
        },
        snapshot: () => {
            const sharedDataNoSession = cloneDeep(this.sharedData)
            delete sharedDataNoSession.session
            return sharedDataNoSession
        },
        restore: (snapshot: any) => {
            const sharedDataSession = this.sharedData.session || {}
            this.sharedData = snapshot || {}
            this.sharedData.session = sharedDataSession
        },
    }

    /**
     * Stockage persistant du peripherique
     */
    public persistant: LocalStorage = {
        get: async (
            key: string,
            optionalSubKeysAttribut?: string[] | undefined
        ) => {
            if (this.platform === "web") {
                return Promise.resolve(localStorage.getItem(key))
            } else {
                const sqliteService =
                    ProviderService.getInstance().get("SQliteService")
                if (!sqliteService) {
                    throw new Error("SQliteService not found")
                }

                if (optionalSubKeysAttribut) {
                    const trimedKeyData = await sqliteService
                        .getInstance()
                        .get(key)
                    if (typeof trimedKeyData === "object") {
                        return StructUtils.getValueFromArray(
                            trimedKeyData,
                            optionalSubKeysAttribut
                        )
                    }
                    return null
                }
                return sqliteService.getInstance().get(key)
            }
        },
        set: async (key: string, data: any) => {
            if (this.platform === "web") {
                return Promise.resolve(localStorage.setItem(key, data))
            } else {
                const sqliteService =
                    ProviderService.getInstance().get("SQliteService")
                if (!sqliteService) {
                    throw new Error("SQliteService not found")
                }
                return sqliteService.getInstance().set(key, data)
            }
        },

        delete: async (key: string) => {
            if (this.platform === "web") {
                return Promise.resolve(localStorage.removeItem(key))
            } else {
                const sqliteService =
                    ProviderService.getInstance().get("SQliteService")
                if (!sqliteService) {
                    throw new Error("SQliteService not found")
                }
                return sqliteService.getInstance().delete(key)
            }
        },
    }

    /**
     * Supprime toute les données locales
     * de sharedData sauf la session
     */
    public clearSharedData(): void {
        this.sharedData = { session: this.sharedData.session }
    }

    /**
     * Supprime toute les données de session
     */
    public clearSessionData(): void {
        this.sharedData = { session: {} }
    }

    /**
     * Prend une clef et un endPoint name sous forme
     * de string et renvoie les entitées correspondantes
     * @param key clef du endpoint
     * @param source source du endpoint
     */
    public static getSourceEndPoint(
        key: string,
        source?: string
    ): {
        source: Service | undefined
        endPoint: ((...args: any[]) => Promise<any>) | undefined
    } {
        // récuperation du type de service
        let sourcePrefix: string | undefined = source
        let trimedKey: string = key

        if (key && !sourcePrefix) {
            const splitedKey = key.split(".")

            // source.key la taille doit etre de 2
            if (splitedKey.length === 2) {
                ;[sourcePrefix, trimedKey] = splitedKey
            }
        }
        if (sourcePrefix && trimedKey) {
            const serviceType: typeof Service | undefined =
                ProviderService.getInstance().get(sourcePrefix)

            // récuperation d l'instance du service
            const service: Service = serviceType?.getInstance()
            // si le service existe
            if (service)
                // récuperation du endPoint
                return {
                    source: service,
                    endPoint: service.fromMap(trimedKey),
                }
        }
        return {
            source: undefined,
            endPoint: undefined,
        }
    }
}

export default StoreService
